import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { api } from "@/lib/apiClient";
import { useAuth } from "@/context/AuthContext";
import { SeverityBadge } from "@/components/SeverityBadge";
import { FileText, LogOut, Shield, Download, ShieldCheck, Trash2 } from "lucide-react";
import { toast } from "sonner";
import SocialLinks from "@/components/SocialLinks";
import DeleteAccountModal from "@/components/DeleteAccountModal";
import ChartAccessCard from "@/components/ChartAccessCard";
import ChangePasswordCard from "@/components/ChangePasswordCard";
import HealthIndicesDashboard from "@/components/HealthIndicesDashboard";
import ServicesBoughtSection from "@/components/ServicesBoughtSection";
import IndexAlertsBanner from "@/components/IndexAlertsBanner";

export default function Me() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [reports, setReports] = useState([]);
  const [exporting, setExporting] = useState(false);
  const [confirmDel, setConfirmDel] = useState(false);

  useEffect(() => {
    api.get("/reports").then((r) => setReports(r.data));
  }, []);

  const downloadExport = async () => {
    setExporting(true);
    try {
      const resp = await api.get("/me/export", { responseType: "blob" });
      const blob = new Blob([resp.data], { type: "application/zip" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `manodayam-export-${(user?.id || "me").slice(0, 8)}.zip`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 500);
      toast.success("Your data export has been downloaded.");
    } catch (e) {
      toast.error("Could not generate your export. Please try again.");
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="space-y-8" data-testid="me-page">
      <div className="rounded-3xl bg-white border border-[color:var(--mano-border)] p-6 lg:p-8 flex items-center gap-5">
        <div className="h-16 w-16 rounded-2xl bg-[color:var(--mano-primary)] text-white flex items-center justify-center font-display text-xl">
          {(user?.name || "M").slice(0, 1)}
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-display text-xl truncate">{user?.name}</div>
          <div className="text-sm text-[color:var(--mano-ink-soft)] truncate">{user?.email}</div>
          <div className="text-xs uppercase-label mt-1">Role: {user?.role}</div>
        </div>
        {user?.role === "platform_admin" && (
          <Link data-testid="me-admin-link" to="/admin" className="hidden sm:inline-flex items-center gap-1 px-4 py-2 rounded-full border border-[color:var(--mano-border)]">
            <Shield size={14} /> Admin
          </Link>
        )}
      </div>

      <IndexAlertsBanner />

      <HealthIndicesDashboard />

      <section>
        <div className="flex items-center justify-between mb-3">
          <div className="uppercase-label inline-flex items-center gap-1"><FileText size={14} /> My reports</div>
          <span className="text-sm text-[color:var(--mano-ink-soft)]">{reports.length} total</span>
        </div>
        <div className="space-y-2">
          {reports.length === 0 && (
            <div className="rounded-2xl border border-dashed border-[color:var(--mano-border)] p-6 text-center text-[color:var(--mano-ink-soft)]">
              No reports yet. Start with a <Link className="text-[color:var(--mano-primary)] font-semibold" to="/assess">voice screening</Link>.
            </div>
          )}
          {reports.map((r) => (
            <Link
              key={r.id}
              to={`/report/${r.id}`}
              data-testid={`me-report-${r.id}`}
              className="flex items-center justify-between rounded-xl border border-[color:var(--mano-border)] bg-white p-4 hover:border-[color:var(--mano-primary)] transition-colors"
            >
              <div>
                <div className="font-semibold">
                  {r.kind === "voice" ? `Voice · ${r.category.replace("_", " ")}` : r.questionnaire_name}
                </div>
                <div className="text-xs text-[color:var(--mano-ink-soft)]">{new Date(r.created_at).toLocaleString()}</div>
              </div>
              <SeverityBadge band={r.band} />
            </Link>
          ))}
        </div>
      </section>

      <ServicesBoughtSection />

      <section data-testid="me-social-section">
        <div className="uppercase-label mb-3">Stay connected</div>
        <SocialLinks variant="grid" testid="me-social-links" />
      </section>

      <ChartAccessCard />

      <ChangePasswordCard />

      <section
        data-testid="me-privacy-section"
        className="rounded-3xl bg-white border border-[color:var(--mano-border)] p-6"
      >
        <div className="uppercase-label inline-flex items-center gap-1">
          <ShieldCheck size={14} /> Your data & privacy
        </div>
        <p className="text-sm text-[color:var(--mano-ink-soft)] mt-2 max-w-xl">
          Under GDPR / HIPAA, you can download every record we hold about you at any
          time, or ask us to anonymize your profile. Clinical records are retained per
          medical record-keeping rules but detached from your identity on deletion.
        </p>
        <div className="mt-4 flex flex-wrap gap-3">
          <button
            data-testid="me-export-btn"
            onClick={downloadExport}
            disabled={exporting}
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-[color:var(--mano-primary)] hover:bg-[color:var(--mano-primary-hover)] text-white font-semibold text-sm disabled:opacity-60"
          >
            <Download size={14} /> {exporting ? "Preparing…" : "Download my data"}
          </button>
          <button
            data-testid="me-delete-btn"
            onClick={() => setConfirmDel(true)}
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full border border-red-300 text-red-700 hover:bg-red-50 font-semibold text-sm"
          >
            <Trash2 size={14} /> Delete my account
          </button>
        </div>
      </section>

      <button
        data-testid="me-logout-btn"
        onClick={logout}
        className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full border border-[color:var(--mano-border)] hover:bg-[color:var(--mano-border)]/40"
      >
        <LogOut size={14} /> Log out
      </button>

      {confirmDel && (
        <DeleteAccountModal
          email={user?.email}
          onClose={() => setConfirmDel(false)}
          onDeleted={async () => {
            setConfirmDel(false);
            await logout();
            nav("/login");
          }}
        />
      )}
    </div>
  );
}
